/* ==========================================================================
   KOLP — Bundle builder
   <bundle-builder> custom element. Pick a pack, configure each shirt
   (color + size) independently, add the real variants to the Shopify cart
   with bundle line-item properties.

   Depends on: bundle-pricing.js, kolp-core.js
   ========================================================================== */
(function () {
  'use strict';
  var KOLP = window.KOLP || {};
  var P = window.KOLPPricing;
  if (!P) { console.warn('KOLP: bundle-pricing.js missing'); return; }

  var COLOR_CYCLE_SEED = ['Black', 'Navy', 'White', 'Olive', 'Sand', 'Charcoal', 'Light Grey'];

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      try {
        this.product = JSON.parse(this.querySelector('.bb-product').textContent);
      } catch (e) { console.error('KOLP: invalid product JSON', e); return; }

      var cfgEl = this.querySelector('.bb-config');
      if (cfgEl) {
        try { P.configure(JSON.parse(cfgEl.textContent)); } catch (e) {}
      }
      // Live Shopify Markets conversion rate (e.g. ~390 for HUF in the
      // Hungary market, 1 in EUR markets) — makes every price/threshold
      // above currency-aware with no further wiring per market.
      try {
        if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
          P.configure({ currencyRate: window.Shopify.currency.rate });
        }
      } catch (e) {}

      this.packsWrap = this.querySelector('.bb-packs');
      this.builderWrap = this.querySelector('[data-bb-builder]');
      this.ctaBtn = this.querySelector('[data-bb-cta]');
      this.ctaPrice = this.querySelectorAll('[data-bb-total]');
      this.perShirtEls = this.querySelectorAll('[data-bb-per]');
      this.wasEls = this.querySelectorAll('[data-bb-was]');
      this.pctEls = this.querySelectorAll('[data-bb-pct]');
      this.errorEl = this.querySelector('[data-bb-error]');
      this.shipWrap = this.querySelector('[data-bb-shipping]');
      // Translated shipping-progress copy (see the data-i18n-* attributes
      // Liquid renders on this element) with plain English fallbacks.
      this.i18nShipUnlocked = (this.shipWrap && this.shipWrap.dataset.i18nUnlocked) || 'This pack qualifies for free EU shipping.';
      this.i18nShipProgress = (this.shipWrap && this.shipWrap.dataset.i18nProgress) || "You're __AMOUNT__ away from free shipping.";

      // Translated builder copy (see the data-i18n-* attributes on <bundle-builder>
      // in main-product-bundle.liquid) with plain English fallbacks.
      this.i18nSelectColorSize = this.dataset.i18nSelectColorSize || 'Select an available color & size';
      this.i18nSelectSize = this.dataset.i18nSelectSize || 'Select an available size';
      // Text-option products (custom.option_style = text, e.g. a Left/Right
      // knee brace): the first option is not a colour, so its chips show the
      // value name and the copy names the option instead of "colour".
      this.asText = !!this.product.colorsAsText;
      this.optionLabel = this.product.colorLabel || (this.dataset.i18nColor || 'Color');
      this.i18nSelectOptionSize = (this.dataset.i18nSelectOptionSize || 'Select an available __OPTION__ & size').replace('__OPTION__', this.optionLabel);
      this.i18nUseSameOptionForEvery = (this.dataset.i18nUseSameOptionForEvery || 'Use the same __OPTION__ for every __NOUN__').replace('__OPTION__', this.optionLabel);
      this.i18nValidationChooseOption = (this.dataset.i18nValidationChooseOption || '__ITEM__: choose __OPTION__ and size.').replace('__OPTION__', this.optionLabel);
      this.i18nColor = this.dataset.i18nColor || 'Color';
      this.i18nSize = this.dataset.i18nSize || 'Size';
      this.i18nSizeGuide = this.dataset.i18nSizeGuide || 'Size guide';
      this.i18nSizeForEvery = this.dataset.i18nSizeForEvery || 'Size for every __NOUN__';
      this.i18nSetSizeForEvery = this.dataset.i18nSetSizeForEvery || 'Set one size for every __NOUN__';
      this.i18nUseSameColorForEvery = this.dataset.i18nUseSameColorForEvery || 'Use the same colour for every __NOUN__';
      this.i18nBulkHint = this.dataset.i18nBulkHint || 'Set a starting point here, then fine-tune any __NOUN__ below.';
      this.i18nValidationChoose = this.dataset.i18nValidationChoose || '__ITEM__: choose a color and size.';
      this.i18nValidationUnavailable = this.dataset.i18nValidationUnavailable || '__ITEM__: __COLOR__ / __SIZE__ is unavailable.';
      this.i18nAddPackNamed = this.dataset.i18nAddPackNamed || 'Add __PACK__ — __PRICE__';
      this.i18nFinishYourPack = this.dataset.i18nFinishYourPack || '<strong>Finish your pack:</strong> ';
      this.i18nAddToCartError = this.dataset.i18nAddToCartError || 'Could not add to cart: __MESSAGE__ Please try again.';
      this.i18nOfflineError = this.dataset.i18nOfflineError || 'You appear to be offline. Check your connection and try again.';
      this.i18nSavePrefix = this.dataset.i18nSavePrefix || 'Save';
      this.i18nPerNoun = this.dataset.i18nPerNoun || '/ __NOUN__';
      this.i18nPackDefaultName = this.dataset.i18nPackDefaultName || '__QTY__-Pack';

      // What one item is called (t-shirt product vs boxers vs …).
      this.noun = this.dataset.itemNoun || 'shirt';
      this.nounCap = this.noun.charAt(0).toUpperCase() + this.noun.slice(1);
      this.nounPlural = this.dataset.itemNounPlural || (this.noun + 's');

      this.state = {
        pack: parseInt(this.dataset.defaultPack, 10) || 5,
        defaultSize: this.dataset.defaultSize || 'M',
        sameColor: false,
        shirts: []
      };
      if (!P.getPack(this.state.pack)) {
        this.state.pack = P.getConfig().packs[0].qty;
      }

      this.buildVariantIndex();
      this.initShirts(this.state.pack);
      this.bindPacks();
      this.updatePackCards();
      this.renderBuilder();
      this.update();

      KOLP.analytics && KOLP.analytics.track('view_item', {
        item_id: this.product.id, item_name: this.product.title
      });
    }

    /* ---- variant lookup -------------------------------------------------- */
    buildVariantIndex() {
      this.byKey = {};
      this.availableColors = new Set();
      this.availableSizes = new Set();
      (this.product.variants || []).forEach(function (v) {
        this.byKey[v.color + '|::|' + v.size] = v;
        if (v.available) { this.availableColors.add(v.color); this.availableSizes.add(v.size); }
      }, this);
    }
    variant(color, size) { return this.byKey[color + '|::|' + size] || null; }
    isAvailable(color, size) {
      var v = this.variant(color, size);
      return !!(v && v.available);
    }
    colorImage(color) {
      var imgs = this.product.imagesByColor || {};
      if (imgs[color] && imgs[color][0]) return imgs[color][0];
      return this.product.defaultImage || '';
    }

    /* ---- shirt slots --------------------------------------------------- */
    initShirts(qty) {
      var colors = this.product.colors.map(function (c) { return c.name; });
      var cycle = COLOR_CYCLE_SEED.filter(function (c) { return colors.indexOf(c) !== -1; });
      // The seed is tee-centric; fall back to the product's own palette when it barely overlaps.
      if (cycle.length < 3) cycle = colors;
      var shirts = [];
      for (var i = 0; i < qty; i++) {
        var existing = this.state.shirts[i];
        shirts.push(existing || {
          color: cycle[i % cycle.length],
          size: this.state.defaultSize
        });
      }
      this.state.shirts = shirts;
    }

    setPack(qty) {
      if (qty === this.state.pack) return;
      this.state.pack = qty;
      this.initShirts(qty);
      this.bindPacks();
      this.renderBuilder();
      this.update();
      KOLP.analytics && KOLP.analytics.track('select_bundle', {
        bundle_qty: qty, bundle_value: P.packTotal(qty) / 100
      });
      KOLP.bus.emit('builder:pack', { qty: qty });
    }

    applySizeToAll(size) {
      this.state.defaultSize = size;
      this.state.shirts.forEach(function (s) { s.size = size; });
      this.syncBuilder();
      this.update();
      KOLP.analytics && KOLP.analytics.track('select_size', { size: size, scope: 'all' });
    }

    applyColorToAll(color) {
      this.state.shirts.forEach(function (s) { s.color = color; });
      this.syncBuilder();
      this.update();
      KOLP.analytics && KOLP.analytics.track('select_color', { color: color, scope: 'all' });
    }

    setShirt(index, patch) {
      Object.assign(this.state.shirts[index], patch);
      if (this.state.sameColor && patch.color) {
        this.state.shirts.forEach(function (s) { s.color = patch.color; });
      }
      this.syncBuilder();
      this.update();
      if (patch.color) {
        KOLP.analytics && KOLP.analytics.track('select_color', { color: patch.color, position: index + 1 });
        KOLP.bus.emit('builder:color', { color: patch.color, position: index + 1 });
      }
      if (patch.size) {
        KOLP.analytics && KOLP.analytics.track('select_size', { size: patch.size, position: index + 1 });
      }
    }

    /* ---- pack cards --------------------------------------------------- */
    bindPacks() {
      if (this._packsBound) {
        KOLP.$$('[data-qty]', this.packsWrap).forEach(function (btn) {
          var q = parseInt(btn.dataset.qty, 10);
          btn.setAttribute('aria-pressed', q === this.state.pack ? 'true' : 'false');
        }, this);
        return;
      }
      this._packsBound = true;
      KOLP.$$('[data-qty]', this.packsWrap).forEach(function (btn) {
        var q = parseInt(btn.dataset.qty, 10);
        btn.setAttribute('aria-pressed', q === this.state.pack ? 'true' : 'false');
        btn.addEventListener('click', function () { this.setPack(q); }.bind(this));
      }, this);
    }

    /* Pack-card prices (total / per-item / savings) are rendered server-side
       from the RAW base-currency (EUR) settings value — Liquid's `money`
       filter formats a plain number in the page's currency but does not run
       it through the Markets conversion rate the way a real variant/cart
       price does. So on any market where that rate isn't 1 (e.g. HUF, ~370x)
       the server-rendered figures are correspondingly off. Correct them
       here using the same KOLPPricing config the CTA price above already
       uses, once at init — the card contents don't change with color/size
       selection, only with which pack is picked, and that's a full re-render
       via setPack(), which doesn't touch these cards' markup. */
    updatePackCards() {
      var self = this;
      KOLP.$$('[data-qty]', this.packsWrap).forEach(function (btn) {
        var q = parseInt(btn.dataset.qty, 10);
        if (!q || !P.getPack(q)) return;
        var priceEl = btn.querySelector('.bundle-option__price');
        var perEl = btn.querySelector('.bundle-option__per');
        var saveEl = btn.querySelector('.bundle-option__save');
        if (priceEl) priceEl.textContent = P.formatMoney(P.packTotal(q));
        if (perEl) perEl.textContent = P.formatMoney(P.pricePerShirt(q)) + ' ' + self.i18nPerNoun.replace('__NOUN__', self.noun);
        if (saveEl) {
          var sv = P.savings(q);
          if (sv.amount > 0) {
            saveEl.textContent = self.i18nSavePrefix + ' ' + P.formatMoney(sv.amount);
          } else {
            saveEl.remove();
          }
        }
      });
    }

    /* ---- builder rendering ------------------------------------------- */
    renderBuilder() {
      var colors = this.product.colors;
      var sizes = this.product.sizes;
      // A product that comes in one colour (the compression socks) has nothing to
      // choose: the swatch row is dropped and the row summary names the size alone.
      var oneColor = colors.length < 2;
      var s = this.state;
      var self = this;

      var bulkSizes = sizes.map(function (sz) {
        var avail = self.availableSizes.has(sz);
        return '<button type="button" class="size-btn" data-bulk-size="' + sz + '"'
          + (sz === s.defaultSize ? ' aria-pressed="true"' : ' aria-pressed="false"')
          + (avail ? '' : ' data-unavailable="true" disabled')
          + '>' + sz + '</button>';
      }).join('');

      var shirtsHtml = s.shirts.map(function (shirt, i) {
        var incomplete = !self.isAvailable(shirt.color, shirt.size);
        var img = self.colorImage(shirt.color);
        var swatches = colors.map(function (c) {
          var canPick = self.isAvailable(c.name, shirt.size) || !self.availableSizes.has(shirt.size);
          if (self.asText) {
            return '<button type="button" class="swatch swatch--text" role="radio"'
              + ' aria-checked="' + (c.name === shirt.color ? 'true' : 'false') + '"'
              + ' aria-pressed="' + (c.name === shirt.color ? 'true' : 'false') + '"'
              + ' data-shirt="' + i + '" data-color="' + escapeAttr(c.name) + '"'
              + (canPick ? '' : ' data-unavailable="true"') + '>' + escapeAttr(c.name) + '</button>';
          }
          return '<button type="button" class="swatch swatch--builder" role="radio"'
            + ' aria-checked="' + (c.name === shirt.color ? 'true' : 'false') + '"'
            + ' aria-pressed="' + (c.name === shirt.color ? 'true' : 'false') + '"'
            + ' aria-label="' + c.name + '"'
            + ' title="' + c.name + '"'
            + ' style="--dot:' + (c.hex || '#ccc') + '"'
            + ' data-shirt="' + i + '" data-color="' + escapeAttr(c.name) + '"'
            + (canPick ? '' : ' data-unavailable="true"') + '></button>';
        }).join('');
        var sizeBtns = sizes.map(function (sz) {
          var avail = self.isAvailable(shirt.color, sz);
          return '<button type="button" class="size-btn"'
            + ' aria-pressed="' + (sz === shirt.size ? 'true' : 'false') + '"'
            + ' data-shirt="' + i + '" data-size="' + sz + '"'
            + (avail ? '' : ' data-unavailable="true"')
            + '>' + sz + '</button>';
        }).join('');

        var configText = incomplete
          ? (oneColor ? self.i18nSelectSize : (self.asText ? self.i18nSelectOptionSize : self.i18nSelectColorSize))
          : (oneColor ? shirt.size : shirt.color + ' · ' + shirt.size);

        // Row 1 always starts expanded (regardless of validity) so the
        // per-item color/size pickers are visible on load -- user testing
        // showed people didn't realize each shirt is customizable when
        // every row starts collapsed.
        var openByDefault = incomplete || i === 0;

        return ''
          + '<details class="shirt' + (incomplete ? ' is-incomplete' : '') + '"' + (openByDefault ? ' open' : '') + ' data-shirt-row="' + i + '">'
          +   '<summary class="shirt__summary">'
          +     '<span class="shirt__thumb" style="background:' + (img ? 'center/cover no-repeat url(' + img + ')' : self.hexFor(shirt.color)) + '" aria-hidden="true"></span>'
          +     '<span class="shirt__meta">'
          +       '<span class="shirt__title">' + self.nounCap + ' ' + (i + 1) + '</span> '
          +       '<span class="shirt__config" data-incomplete="' + (incomplete ? 'true' : 'false') + '">' + configText + '</span>'
          +     '</span>'
          +     iconChevron()
          +   '</summary>'
          +   '<div class="shirt__body">'
          +     (oneColor ? '' : '<div><div class="shirt__field-label" id="clr-' + i + '">' + (self.asText ? escapeAttr(self.optionLabel) : self.i18nColor) + '</div>'
          +       '<div class="swatches" role="radiogroup" aria-labelledby="clr-' + i + '">' + swatches + '</div></div>')
          +     '<div><div class="shirt__field-label" id="sz-' + i + '">' + self.i18nSize + '</div>'
          +       '<div class="sizes" role="group" aria-labelledby="sz-' + i + '">' + sizeBtns + '</div>'
          +       '<button type="button" class="link-underline text-xsmall" data-size-guide style="margin-top:.5rem">' + self.i18nSizeGuide + '</button></div>'
          +   '</div>'
          + '</details>';
      }).join('');

      this.builderWrap.innerHTML = ''
        + '<div class="builder__bulk">'
        +   '<div class="builder__bulk-row">'
        +     '<span class="builder__bulk-label">' + this.i18nSizeForEvery.replace('__NOUN__', this.noun) + '</span>'
        +     '<div class="sizes" role="group" aria-label="' + escapeAttr(this.i18nSetSizeForEvery.replace('__NOUN__', this.noun)) + '">' + bulkSizes + '</div>'
        +   '</div>'
        +   (oneColor ? '' : '<label class="builder__bulk-check">'
        +     '<input type="checkbox" data-same-color' + (s.sameColor ? ' checked' : '') + '>'
        +     '<span>' + (this.asText ? this.i18nUseSameOptionForEvery : this.i18nUseSameColorForEvery).replace('__NOUN__', this.noun) + '</span>'
        +   '</label>')
        +   '<p class="builder__bulk-hint">' + this.i18nBulkHint.replace('__NOUN__', this.noun) + '</p>'
        + '</div>'
        + '<div class="builder__list">' + shirtsHtml + '</div>';

      this.bindBuilderEvents();
      this.syncBuilder();
    }

    /* In-place update — preserves <details open> state, focus and scroll.
       Only pack-size changes trigger a full renderBuilder(). */
    syncBuilder() {
      var self = this;
      var s = this.state;

      KOLP.$$('[data-bulk-size]', this.builderWrap).forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.dataset.bulkSize === s.defaultSize ? 'true' : 'false');
      });

      KOLP.$$('[data-shirt-row]', this.builderWrap).forEach(function (row) {
        var i = parseInt(row.dataset.shirtRow, 10);
        var shirt = s.shirts[i];
        if (!shirt) return;
        var incomplete = !self.isAvailable(shirt.color, shirt.size);

        row.classList.toggle('is-incomplete', incomplete);
        if (incomplete) row.open = true;

        var cfg = row.querySelector('.shirt__config');
        if (cfg) {
          var oneColorRow = self.product.colors.length < 2;  // same rule as renderBuilder
          cfg.textContent = incomplete
            ? (oneColorRow ? self.i18nSelectSize : (self.asText ? self.i18nSelectOptionSize : self.i18nSelectColorSize))
            : (oneColorRow ? shirt.size : shirt.color + ' · ' + shirt.size);
          cfg.setAttribute('data-incomplete', incomplete ? 'true' : 'false');
        }
        var thumb = row.querySelector('.shirt__thumb');
        if (thumb) {
          var img = self.colorImage(shirt.color);
          thumb.style.background = img ? 'center/cover no-repeat url(' + img + ')' : self.hexFor(shirt.color);
        }

        KOLP.$$('.swatch[data-shirt]', row).forEach(function (btn) {
          var isSel = btn.dataset.color === shirt.color;
          btn.setAttribute('aria-checked', isSel ? 'true' : 'false');
          btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
          var canPick = self.isAvailable(btn.dataset.color, shirt.size) || !self.availableSizes.has(shirt.size);
          if (canPick) btn.removeAttribute('data-unavailable');
          else btn.setAttribute('data-unavailable', 'true');
        });
        KOLP.$$('.size-btn[data-shirt]', row).forEach(function (btn) {
          btn.setAttribute('aria-pressed', btn.dataset.size === shirt.size ? 'true' : 'false');
          if (self.isAvailable(shirt.color, btn.dataset.size)) btn.removeAttribute('data-unavailable');
          else btn.setAttribute('data-unavailable', 'true');
        });
      });

      var sc = this.builderWrap.querySelector('[data-same-color]');
      if (sc) sc.checked = s.sameColor;
    }

    hexFor(name) {
      if (this.asText) return 'var(--color-surface)';
      var c = (this.product.colors || []).find(function (x) { return x.name === name; });
      return c ? c.hex : '#ccc';
    }

    bindBuilderEvents() {
      var self = this;
      KOLP.$$('[data-bulk-size]', this.builderWrap).forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.hasAttribute('disabled')) return;
          self.applySizeToAll(btn.dataset.bulkSize);
        });
      });
      var sameColor = this.builderWrap.querySelector('[data-same-color]');
      if (sameColor) sameColor.addEventListener('change', function () {
        self.state.sameColor = sameColor.checked;
        if (sameColor.checked) self.applyColorToAll(self.state.shirts[0].color);
      });
      KOLP.$$('.swatch[data-shirt]', this.builderWrap).forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.dataset.unavailable === 'true') return;
          self.setShirt(parseInt(btn.dataset.shirt, 10), { color: btn.dataset.color });
        });
        keyboardRadio(btn);
      });
      KOLP.$$('.size-btn[data-shirt]', this.builderWrap).forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.dataset.unavailable === 'true') return;
          self.setShirt(parseInt(btn.dataset.shirt, 10), { size: btn.dataset.size });
        });
      });
      KOLP.$$('[data-size-guide]', this.builderWrap).forEach(function (btn) {
        btn.addEventListener('click', function () { KOLP.bus.emit('sizeguide:open'); });
      });
    }

    /* ---- validation + totals --------------------------------------- */
    validate() {
      var problems = [];
      this.state.shirts.forEach(function (shirt, i) {
        var item = this.nounCap + ' ' + (i + 1);
        var v = this.variant(shirt.color, shirt.size);
        if (!v) problems.push({ index: i, reason: (this.asText ? this.i18nValidationChooseOption : this.i18nValidationChoose).replace('__ITEM__', item) });
        else if (!v.available) problems.push({ index: i, reason: this.i18nValidationUnavailable.replace('__ITEM__', item).replace('__COLOR__', shirt.color).replace('__SIZE__', shirt.size) });
      }, this);
      return problems;
    }

    update() {
      var qty = this.state.pack;
      var total = P.packTotal(qty);
      var per = P.pricePerShirt(qty);
      var save = P.savings(qty);
      var problems = this.validate();
      var valid = problems.length === 0;

      this.ctaPrice.forEach(function (el) { el.textContent = P.formatMoney(total); });
      this.perShirtEls.forEach(function (el) { el.textContent = P.formatMoney(per); });
      this.wasEls.forEach(function (el) {
        el.textContent = P.formatMoney(save.fullPrice);
        el.hidden = save.amount <= 0;
      });
      this.pctEls.forEach(function (el) {
        el.textContent = '–' + save.percent + '%';
        el.hidden = save.percent <= 0;
      });

      if (this.ctaBtn) {
        var pack = P.getPack(qty);
        var label = this.i18nAddPackNamed.replace('__PACK__', pack ? pack.name : this.i18nPackDefaultName.replace('__QTY__', qty)).replace('__PRICE__', P.formatMoney(total));
        var idle = this.ctaBtn.querySelector('.atc-btn__label--idle');
        if (idle) idle.textContent = label;
        this.ctaBtn.setAttribute('aria-disabled', valid ? 'false' : 'false'); // allow click to surface errors
      }

      if (this.errorEl) {
        if (valid) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
      }

      // free shipping preview based on this bundle alone
      if (this.shipWrap) this.renderShippingPreview(total);

      // dominant color for gallery
      var counts = {};
      this.state.shirts.forEach(function (s) { counts[s.color] = (counts[s.color] || 0) + 1; });
      var dominant = Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; })[0];

      KOLP.bus.emit('builder:update', {
        pack: qty, total: total, perShirt: per, savings: save, valid: valid, dominantColor: dominant
      });
    }

    renderShippingPreview(total) {
      var threshold = P.freeShippingThreshold();
      var unlocked = P.qualifiesForFreeShipping(total);
      var remaining = P.freeShippingRemaining(total);
      var pct = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 100;
      this.shipWrap.classList.toggle('is-unlocked', unlocked);
      this.shipWrap.innerHTML =
        '<div class="ship-progress__bar"><div class="ship-progress__fill" style="width:' + pct + '%"></div></div>'
        + '<div class="ship-progress__text">'
        + (unlocked ? '✓ ' + this.i18nShipUnlocked
                    : this.i18nShipProgress.replace('__AMOUNT__', P.formatMoney(remaining)))
        + '</div>';
    }

    /* ---- add to cart ---------------------------------------------- */
    async addToCart() {
      var problems = this.validate();
      if (problems.length) {
        this.showErrors(problems);
        return;
      }
      var selection = this.state.shirts.map(function (shirt) {
        return { variantId: this.variant(shirt.color, shirt.size).id, color: shirt.color, size: shirt.size };
      }, this);
      var pack = P.getPack(this.state.pack);
      var items = P.buildCartItems(selection, {
        itemNoun: this.noun,
        itemNounPlural: this.nounPlural,
        lineLabelKey: this.dataset.i18nBundleLineKey,
        lineLabel: this.dataset.i18nBundleLineLabel,
        nounCap: this.nounCap,
        packVariantId: pack && pack.variantId,
        offerName: (this.product && this.product.title ? this.product.title + ' ' : 'KOLP ') + (pack ? pack.name : this.i18nPackDefaultName.replace('__QTY__', this.state.pack))
      });

      this.setBtnState('loading');
      try {
        var res = await fetch((KOLP.routes.cart_add_url || '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        });
        if (!res.ok) {
          var err = await res.json().catch(function () { return {}; });
          throw new Error(err.description || err.message || ('Cart error ' + res.status));
        }
        await res.json();
        this.setBtnState('done');
        KOLP.analytics && KOLP.analytics.track('add_to_cart', {
          bundle_qty: this.state.pack,
          value: P.packTotal(this.state.pack) / 100,
          currency: P.getConfig().currency,
          items: items.length
        });
        KOLP.analytics && KOLP.analytics.track('begin_checkout', {
          value: P.packTotal(this.state.pack) / 100,
          currency: P.getConfig().currency
        });
        // Skip the cart step — send the customer straight to checkout.
        setTimeout(function () { window.location.assign('/checkout'); }, 450);
      } catch (e) {
        this.setBtnState('idle');
        this.showErrors([{ reason: navigator.onLine
          ? this.i18nAddToCartError.replace('__MESSAGE__', e.message)
          : this.i18nOfflineError }], true);
        KOLP.analytics && KOLP.analytics.track('add_to_cart_error', { message: e.message });
      }
    }

    setBtnState(state) {
      if (this.ctaBtn) this.ctaBtn.dataset.state = state;
      KOLP.bus.emit('builder:btnstate', { state: state });
    }

    showErrors(problems, isNetwork) {
      if (!this.errorEl) return;
      this.errorEl.hidden = false;
      this.errorEl.innerHTML = (isNetwork ? '' : this.i18nFinishYourPack)
        + problems.map(function (p) { return escapeHtml(p.reason); }).join('<br>');
      this.errorEl.setAttribute('role', 'alert');
      // open + flag the first incomplete shirt
      var first = problems.find(function (p) { return typeof p.index === 'number'; });
      if (first) {
        var row = this.builderWrap.querySelector('[data-shirt-row="' + first.index + '"]');
        if (row) { row.open = true; row.classList.add('is-incomplete'); row.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      } else {
        this.errorEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }

  /* ---- helpers ------------------------------------------------------- */
  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  }); }
  function escapeAttr(s) { return String(s).replace(/"/g, '&quot;'); }
  function iconChevron() {
    return '<svg class="shirt__chevron" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  }
  function keyboardRadio(btn) {
    btn.addEventListener('keydown', function (e) {
      var group = Array.prototype.slice.call(btn.parentNode.querySelectorAll('.swatch:not([data-unavailable])'));
      var idx = group.indexOf(btn);
      if (['ArrowRight', 'ArrowDown'].indexOf(e.key) !== -1) { e.preventDefault(); (group[idx + 1] || group[0]).click(); (group[idx + 1] || group[0]).focus(); }
      if (['ArrowLeft', 'ArrowUp'].indexOf(e.key) !== -1) { e.preventDefault(); (group[idx - 1] || group[group.length - 1]).click(); (group[idx - 1] || group[group.length - 1]).focus(); }
    });
  }

  customElements.define('bundle-builder', BundleBuilder);

  // Wire the primary + sticky CTA buttons to the active builder
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-bb-cta], [data-sticky-cta]');
    if (!btn) return;
    var builder = document.querySelector('bundle-builder');
    if (builder) { e.preventDefault(); builder.addToCart(); }
  });
})();
